import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BroadcastRecipientsPanel from "@/components/admin/BroadcastRecipientsPanel";
import BroadcastOptOuts from "@/components/admin/BroadcastOptOuts";
import BroadcastBounces from "@/components/admin/BroadcastBounces";
import { Send, Eye, History, FlaskConical, CalendarClock, Tag, XCircle, BookmarkPlus, FolderOpen, Trash2, MailOpen, MousePointerClick } from "lucide-react";

const MERGE_TAGS = [
  { tag: "{{company}}", hint: "Supplier's company name" },
  { tag: "{{first_name}}", hint: "Contact first name (falls back to \"there\")" },
  { tag: "{{claim_link}}", hint: "Each unclaimed supplier's own claim link (claimed accounts get their dashboard link)" },
];
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const AUDIENCES = [
  { id: "all_suppliers", label: "All Suppliers" },
  { id: "unclaimed_suppliers", label: "Unclaimed Suppliers" },
  { id: "all_buyers",    label: "All Buyers" },
  { id: "all_users",     label: "All Users" },
  { id: "starter",       label: "RFQ Starter tier" },
  { id: "growth",        label: "Catalogue Growth tier" },
  { id: "leader",        label: "Category Leader tier" },
  { id: "enterprise",    label: "Enterprise tier" },
];

export default function BroadcastComposer() {
  const [subject, setSubject]     = useState("");
  const [body, setBody]           = useState("");
  const [audience, setAudience]   = useState("all_suppliers");
  const [audienceCount, setAudienceCount] = useState(null);
  const [previewHtml, setPreviewHtml]     = useState("");
  const [showPreview, setShowPreview]     = useState(false);
  const [sending, setSending]     = useState(false);
  // null = still loading (never show a false zero); "error" = request failed, offer retry.
  const [history, setHistory]     = useState(null);
  const [historyError, setHistoryError] = useState("");

  useEffect(() => {
    let stale = false;
    setAudienceCount(null);
    api.get(`/admin/broadcast/audience-count?audience=${audience}`)
      .then(({ data }) => { if (!stale) setAudienceCount(data.count); })
      .catch(() => { if (!stale) setAudienceCount(null); });
    return () => { stale = true; };
  }, [audience]);

  const loadHistory = async () => {
    setHistoryError("");
    try {
      const { data } = await api.get("/admin/broadcast/history?limit=50");
      setHistory(data.items || []);
    } catch (e) {
      setHistoryError(e.response?.status === 401 ? "Session expired — sign in again." : (e.response?.data?.detail || "Couldn't load broadcast history."));
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const [showTest, setShowTest] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSending, setTestSending] = useState(false);
  const doTestSend = async (e) => {
    e?.preventDefault();
    setTestSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/test-send", { subject, body, to_email: testEmail });
      toast.success(`Test email sent to ${data.to}`);
      setShowTest(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test send failed");
    } finally { setTestSending(false); }
  };

  const doPreview = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and body first"); return; }
    try {
      const { data } = await api.post("/admin/broadcast/preview", { subject, body });
      setPreviewHtml(data.html);
      setShowPreview(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const [reportFor, setReportFor] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const loadTemplates = () => api.get("/admin/broadcast/templates").then(({ data }) => setTemplates(data.items || [])).catch(() => {});
  useEffect(() => { loadTemplates(); }, []);
  const saveTemplate = async (e) => {
    e?.preventDefault();
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and body first"); return; }
    setSavingTemplate(true);
    try {
      const { data } = await api.post("/admin/broadcast/templates", { name: templateName, subject, body });
      toast.success(data.replaced ? `Template "${data.name}" updated` : `Template "${data.name}" saved`);
      setShowSaveTemplate(false); setTemplateName("");
      loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save template");
    } finally { setSavingTemplate(false); }
  };
  const loadTemplate = (t) => {
    if ((subject.trim() || body.trim()) && !window.confirm(`Replace the current subject and body with template "${t.name}"?`)) return;
    setSubject(t.subject); setBody(t.body); setShowPreview(false);
    toast.success(`Loaded "${t.name}"`);
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/admin/broadcast/templates/${t.id}`); loadTemplates(); } catch (err) { toast.error("Could not delete template"); }
  };

  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const scheduleDate = scheduleOn && scheduleAt ? new Date(scheduleAt) : null;
  const scheduleInvalid = scheduleOn && (!scheduleDate || isNaN(scheduleDate) || scheduleDate.getTime() < Date.now() + 60000);
  const usesClaimLink = /(\{\{|%7B%7B)\s*claim_link\s*(\}\}|%7D%7D)/i.test(subject + body);
  const insertTag = (tag) => setBody((b) => (b ? `${b}${/\s$/.test(b) ? "" : " "}${tag}` : tag));

  const doSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and body first"); return; }
    if (scheduleInvalid) { toast.error("Pick a scheduled time at least a minute in the future"); return; }
    const who = `${audienceCount ?? "the selected"} recipient(s)`;
    const msg = scheduleDate
      ? `Schedule this announcement to ${who}?\n\nIt will go out on ${fmtWhen(scheduleDate.toISOString())} (your local time).`
      : `Send this announcement to ${who} now?`;
    if (!window.confirm(msg)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/send", { subject, body, audience, scheduled_for: scheduleDate ? scheduleDate.toISOString() : null });
      if (data.status === "scheduled") toast.success(`Scheduled for ${fmtWhen(data.scheduled_for)}`);
      else toast.success(`Sent to ${data.recipient_count} recipient(s)${data.failed_count ? `, ${data.failed_count} failed` : ""}`);
      setSubject(""); setBody(""); setShowPreview(false); setPreviewHtml(""); setScheduleOn(false); setScheduleAt("");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const cancelScheduled = async (b) => {
    if (!window.confirm(`Cancel the broadcast "${b.subject}" scheduled for ${fmtWhen(b.scheduled_for)}?`)) return;
    try { await api.delete(`/admin/broadcast/${b.id}`); toast.success("Scheduled broadcast cancelled"); loadHistory(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-broadcast-composer">
      <div className="bg-white border border-[#E8ECE9] p-6">
        <div className="tracking-overline text-[10px] text-[#264839] mb-3">Send announcement</div>
        <h2 className="font-serif text-2xl text-[#11211A] mb-4">Compose broadcast email</h2>

        <div className="mb-4 flex flex-wrap items-center gap-2 bg-[#FAFAF7] border border-[#E8ECE9] px-3 py-2" data-testid="broadcast-templates">
          <span className="text-[10px] uppercase tracking-widest text-[#7D8C85] inline-flex items-center gap-1"><FolderOpen className="w-3 h-3" /> Templates</span>
          {templates.length === 0 && <span className="text-[11px] text-[#7D8C85]">None saved yet — write a broadcast, then “Save as template”.</span>}
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-stretch border border-[#C4CFC9] bg-white text-[11px]" data-testid={`broadcast-template-${t.id}`}>
              <button type="button" onClick={() => loadTemplate(t)} title={t.subject} className="px-2 py-0.5 text-[#264839] hover:bg-[#264839] hover:text-white transition" data-testid={`broadcast-template-load-${t.id}`}>{t.name}</button>
              <button type="button" onClick={() => deleteTemplate(t)} aria-label={`Delete template ${t.name}`} className="px-1.5 border-l border-[#E8ECE9] text-[#9AA6A0] hover:text-[#B35C4A]" data-testid={`broadcast-template-delete-${t.id}`}><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
          <span className="flex-1" />
          {showSaveTemplate ? (
            <form onSubmit={saveTemplate} className="flex items-center gap-2" data-testid="broadcast-template-save-form">
              <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} required maxLength={80} placeholder="Template name, e.g. Claim nudge" autoFocus className="px-2 py-1 border border-[#C4CFC9] text-xs bg-white w-56" data-testid="broadcast-template-name" />
              <button type="submit" disabled={savingTemplate || !templateName.trim()} className="btn-primary text-[10px] py-1 px-3 disabled:opacity-50" data-testid="broadcast-template-save">{savingTemplate ? "Saving…" : "Save"}</button>
              <button type="button" onClick={() => setShowSaveTemplate(false)} className="text-[11px] text-[#7D8C85] hover:text-[#11211A]" data-testid="broadcast-template-save-cancel">Cancel</button>
            </form>
          ) : (
            <button type="button" onClick={() => setShowSaveTemplate(true)} disabled={!subject.trim() || !body.trim()} title={!subject.trim() || !body.trim() ? "Add a subject and body first" : "Save the current subject and body"} className="text-[11px] text-[#264839] inline-flex items-center gap-1 hover:underline disabled:opacity-40 disabled:no-underline" data-testid="broadcast-template-save-btn"><BookmarkPlus className="w-3.5 h-3.5" /> Save as template</button>
          )}
        </div>

        <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Subject</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={250}
          placeholder="E.g. New badge unlocked · faster payouts for Category Leaders"
          className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white mb-4"
          data-testid="broadcast-subject"
        />

        <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">
          Body — supports <code className="font-mono text-[11px] bg-[#F0EFEA] px-1">**bold**</code>, <code className="font-mono text-[11px] bg-[#F0EFEA] px-1">*italic*</code>, <code className="font-mono text-[11px] bg-[#F0EFEA] px-1">[link](url)</code>, blank lines split paragraphs
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          maxLength={20000}
          placeholder={"Hi supplier,\n\nWe just shipped **auto-refill for Quote Credits** — you can turn it on from your dashboard.\n\n[Manage settings](https://accommodationsuppliers.com/dashboard)"}
          className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white font-mono"
          data-testid="broadcast-body"
        />
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="broadcast-merge-tags">
          <span className="text-[10px] uppercase tracking-widest text-[#7D8C85] inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Merge tags</span>
          {MERGE_TAGS.map((t) => (
            <button key={t.tag} type="button" onClick={() => insertTag(t.tag)} title={t.hint} className="font-mono text-[11px] px-2 py-0.5 border border-[#C4CFC9] bg-[#FAFAF7] text-[#264839] hover:border-[#264839] transition" data-testid={`broadcast-tag-${t.tag.replace(/[{}]/g, "")}`}>{t.tag}</button>
          ))}
          <span className="text-[11px] text-[#7D8C85]">Work in subject and body · replaced per recipient at send time.</span>
        </div>
        {usesClaimLink && audience !== "unclaimed_suppliers" && (
          <div className="mt-2 text-[11px] text-[#8A6508] bg-[#FBF5E6] border border-[#B8860B]/40 px-3 py-2" data-testid="broadcast-claim-link-warning">
            {"{{claim_link}}"} is personalised for unclaimed suppliers. For this audience, already-claimed accounts will receive a link to their dashboard instead.
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Audience</label>
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white"
              data-testid="broadcast-audience"
            >
              {AUDIENCES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </select>
            <div className="text-[11px] text-[#7D8C85] mt-1" data-testid="broadcast-audience-count">
              {audienceCount === null ? "…" : `${audienceCount} recipient${audienceCount === 1 ? "" : "s"}`}
            </div>
          </div>

          <div className="min-w-[220px]" data-testid="broadcast-schedule">
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold cursor-pointer">
              <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} data-testid="broadcast-schedule-toggle" />
              <CalendarClock className="w-3.5 h-3.5" /> Send later
            </label>
            {scheduleOn ? (
              <>
                <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)} className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="broadcast-schedule-at" />
                <div className={`text-[11px] mt-1 ${scheduleInvalid ? "text-[#B35C4A]" : "text-[#264839]"}`} data-testid="broadcast-schedule-summary">
                  {!scheduleAt ? "Choose a date and time" : scheduleInvalid ? "Must be at least a minute from now" : `Goes out ${fmtWhen(scheduleDate.toISOString())} (your local time)`}
                </div>
              </>
            ) : <div className="text-[11px] text-[#7D8C85] mt-2">Sends immediately</div>}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={doPreview}
              className="border border-[#264839] text-[#264839] px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#264839] hover:text-white transition inline-flex items-center gap-1.5"
              data-testid="broadcast-preview-btn"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
            <button
              type="button"
              onClick={() => setShowTest((v) => !v)}
              disabled={!subject.trim() || !body.trim()}
              title={!subject.trim() || !body.trim() ? "Add a subject and body first" : "Email a single test copy to yourself"}
              className="border border-[#B8860B] text-[#8A6508] px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#B8860B] hover:text-white transition disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="broadcast-test-btn"
            >
              <FlaskConical className="w-3.5 h-3.5" /> Send test
            </button>
            <button
              type="button"
              onClick={doSend}
              disabled={sending || !subject.trim() || !body.trim() || scheduleInvalid}
              className="bg-[#B35C4A] text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#9E4E3E] transition disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="broadcast-send-btn"
            >
              {scheduleOn ? <CalendarClock className="w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />} {sending ? (scheduleOn ? "Scheduling…" : "Sending…") : (scheduleOn ? "Schedule" : "Send")}
            </button>
          </div>
        </div>
      </div>

      {showTest && (
        <form onSubmit={doTestSend} className="bg-[#FBF5E6] border border-[#B8860B]/40 p-4 flex flex-wrap items-end gap-3" data-testid="broadcast-test-panel">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs uppercase tracking-widest text-[#8A6508] mb-1 font-semibold">Send a test copy to</label>
            <input type="email" required value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@accommodationsuppliers.com" autoFocus className="w-full px-3 py-2 border border-[#C4CFC9] text-sm bg-white" data-testid="broadcast-test-email" />
            <div className="text-[11px] text-[#7D8C85] mt-1">One email, subject prefixed “[TEST]”, with the current subject and body. Merge tags use sample values (Sample Supplier Co / Alex / sample claim link). Not recorded in history.</div>
          </div>
          <button type="submit" disabled={testSending || !testEmail.trim()} className="bg-[#B8860B] text-white px-4 py-2 text-xs uppercase tracking-widest font-semibold hover:bg-[#9A7009] transition disabled:opacity-50" data-testid="broadcast-test-send">
            {testSending ? "Sending…" : "Send test email"}
          </button>
          <button type="button" onClick={() => setShowTest(false)} className="text-xs text-[#7D8C85] hover:text-[#11211A] px-2 py-2" data-testid="broadcast-test-cancel">Cancel</button>
        </form>
      )}

      {showPreview && (
        <div className="bg-white border border-[#E8ECE9] p-6" data-testid="broadcast-preview-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="tracking-overline text-[10px] text-[#264839]">Preview</div>
            <button onClick={() => setShowPreview(false)} className="text-xs text-[#7D8C85] hover:text-[#11211A]" data-testid="broadcast-preview-close">Close preview</button>
          </div>
          <div className="border border-[#E8ECE9] max-h-[520px] overflow-y-auto">
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E8ECE9] p-6" data-testid="broadcast-history">
        <div className="tracking-overline text-[10px] text-[#264839] mb-3 inline-flex items-center gap-1.5">
          <History className="w-3 h-3" /> Broadcast history
        </div>
        <h2 className="font-serif text-2xl text-[#11211A] mb-1">Recent announcements</h2>
        <p className="text-xs text-[#7D8C85] mb-4">Click a sent broadcast to see who opened and clicked, and download the list as CSV.</p>
        {historyError ? (
          <div className="text-sm text-[#B35C4A] flex items-center gap-3" data-testid="broadcast-history-error">{historyError} <button type="button" onClick={loadHistory} className="text-xs underline text-[#264839]" data-testid="broadcast-history-retry">Retry</button></div>
        ) : history === null ? (
          <div className="text-sm text-[#7D8C85] animate-pulse" data-testid="broadcast-history-loading">Loading broadcast history…</div>
        ) : history.length === 0 ? (
          <div className="text-sm text-[#7D8C85]" data-testid="broadcast-history-empty">No broadcasts yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#7D8C85] border-b border-[#E8ECE9]">
              <tr><th className="py-2">Sent / scheduled</th><th>Subject</th><th>Audience</th><th className="text-right">Recipients</th><th className="text-right"><span className="inline-flex items-center gap-1 justify-end"><MailOpen className="w-3 h-3" /> Opens</span></th><th className="text-right"><span className="inline-flex items-center gap-1 justify-end"><MousePointerClick className="w-3 h-3" /> Clicks</span></th><th></th></tr>
            </thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id} onClick={() => b.tracked && setReportFor(b)} title={b.tracked ? "View per-recipient report" : undefined} className={`border-b border-[#F0EFEA] ${b.tracked ? "cursor-pointer hover:bg-[#FAFAF7]" : ""} ${reportFor?.id === b.id ? "bg-[#F0EFEA]" : ""}`} data-testid={`broadcast-row-${b.id}`}>
                  <td className="py-2 text-[#7D8C85] tabular-nums">
                    {b.status === "scheduled"
                      ? <span className="inline-flex items-center gap-1 text-[#8A6508]" data-testid={`broadcast-scheduled-${b.id}`}><CalendarClock className="w-3 h-3" /> {fmtWhen(b.scheduled_for)}</span>
                      : b.status === "cancelled" ? <span className="line-through">{fmtWhen(b.scheduled_for)}</span>
                      : (b.sent_at || "").replace("T", " ").slice(0, 16)}
                    {b.status && b.status !== "sent" && <span className={`ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${b.status === "scheduled" ? "bg-[#FBF5E6] text-[#8A6508]" : b.status === "failed" ? "bg-[#FBEAE6] text-[#B35C4A]" : "bg-[#F0EFEA] text-[#7D8C85]"}`}>{b.status}</span>}
                  </td>
                  <td className="py-2 text-[#11211A]">{b.subject}</td>
                  <td className="py-2 text-[#7D8C85]">{b.audience_label || b.audience}</td>
                  <td className="py-2 text-right text-[#11211A] tabular-nums">
                    {b.status === "scheduled" ? "—" : b.recipient_count}
                    {b.failed_count > 0 && <span className="text-[#B35C4A] ml-1 text-xs">({b.failed_count} failed)</span>}
                  </td>
                  <td className="py-2 text-right tabular-nums" data-testid={`broadcast-opens-${b.id}`}>
                    {b.status === "scheduled" ? "—" : !b.tracked ? <span className="text-[#C4CFC9]" title="Sent before tracking was added">n/a</span>
                      : <>{b.open_count}{b.recipient_count ? <span className="text-[#7D8C85] text-xs ml-1">({Math.round((b.open_count / b.recipient_count) * 100)}%)</span> : null}</>}
                  </td>
                  <td className="py-2 text-right tabular-nums" data-testid={`broadcast-clicks-${b.id}`}>
                    {b.status === "scheduled" ? "—" : !b.tracked ? <span className="text-[#C4CFC9]">n/a</span>
                      : <>{b.click_count}{b.recipient_count ? <span className="text-[#7D8C85] text-xs ml-1">({Math.round((b.click_count / b.recipient_count) * 100)}%)</span> : null}</>}
                  </td>
                  <td className="py-2 text-right">
                    {b.status === "scheduled" && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); cancelScheduled(b); }} className="text-[11px] text-[#B35C4A] hover:underline inline-flex items-center gap-1" data-testid={`broadcast-cancel-${b.id}`}><XCircle className="w-3 h-3" /> Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {reportFor && <BroadcastRecipientsPanel broadcast={reportFor} onClose={() => setReportFor(null)} />}

      <BroadcastOptOuts />

      <BroadcastBounces />
    </div>
  );
}
